import classNames from "classnames";
import React from "react";
import { getBackgroundUrl } from "../../utils/get-background-url";
import { ExpandIcon, GridIcon, TrashIcon } from "../Icons";
import { ImagePlaceholder } from "../ImagePlaceholder";
import { getOptimizedImagePathFromUrl, OptimizedImage, TImageFit } from "../OptimizedImage";
import "./image-container.scss";
import { getWindow } from "../../utils";

type TAction = "enlarge" | "delete" | "see-more";

export type TImageContainer = {
    imgUrl?: string | ArrayBuffer;
    imgSize?: TImageFit;
    aspectRatio?: string;
    useOptimizedImage?: boolean;
    actions?: TAction[];
    onActionClick?: (action: TAction) => void;
    actionsConfig?: {
        position?: "top-right" | "bottom-left";
        direction?: "row" | "column";
    };
    fallbackUrl?: string;
    noBorderRadius?: boolean;
    fillParent?: boolean;
    disabled?: boolean;
    onImageClick?: () => void;
    altText?: string;
};

export const ImageContainer = ({
    imgUrl,
    imgSize = "cover",
    aspectRatio = "16 / 9",
    useOptimizedImage = true,
    actions,
    onActionClick,
    actionsConfig,
    fallbackUrl,
    noBorderRadius = false,
    fillParent = false,
    disabled = false,
    onImageClick,
    altText = "",
}: TImageContainer) => {
    const optimizedImagepath = getOptimizedImagePathFromUrl(imgUrl as string);
    const aspectRatioX = parseInt(aspectRatio.split("/")[0]);
    const aspectRatioY = parseInt(aspectRatio.split("/")[1]);
    const mergedActionsConfig = { position: "top-right", direction: "column", ...actionsConfig };

    const handleActionClick = (e: React.MouseEvent, action: TAction) => {
        if (disabled) {
            return;
        }

        e.stopPropagation();
        onActionClick?.(action);
    };

    const getActionAriaLabel = (action: TAction): string => {
        const texts = getWindow().__TEXTS__;

        switch (action) {
            case "enlarge":
                return texts?.["enlarge-image"] || "Enlarge image";
            case "delete":
                return texts?.["delete-image"] || "Delete image";
            case "see-more":
                return texts?.["see-more-images"] || "See more images";
            default:
                return "";
        }
    };

    return (
        <div
            className={classNames("c-image-container", {
                "c-image-container--no-border-radius": noBorderRadius,
                "c-image-container--disabled": disabled,
                "c-image-container--clickable": onImageClick,
            })}
            style={{ aspectRatio: aspectRatio }}
            onClick={onImageClick}
        >
            {useOptimizedImage ? (
                <OptimizedImage
                    path={optimizedImagepath}
                    fillParent={fillParent}
                    aspectX={aspectRatioX}
                    aspectY={aspectRatioY}
                    imageFit={imgSize}
                    fallbackUrl={fallbackUrl}
                    altText={altText}
                />
            ) : (
                <>
                    {!imgUrl ? (
                        <ImagePlaceholder fillParent />
                    ) : (
                        <>
                            <div
                                className="c-image-container__img"
                                style={{
                                    aspectRatio: aspectRatio,
                                    backgroundImage: getBackgroundUrl((imgUrl as string) ?? fallbackUrl),
                                    backgroundSize: imgSize,
                                }}
                            ></div>
                        </>
                    )}
                </>
            )}
            {actions && (
                <div
                    className={classNames("c-image-container__actions", {
                        "c-image-container__actions--top-right": mergedActionsConfig.position === "top-right",
                        "c-image-container__actions--bottom-left": mergedActionsConfig.position === "bottom-left",
                        "c-image-container__actions--row": mergedActionsConfig.direction === "row",
                        "c-image-container__actions--column": mergedActionsConfig.direction === "column",
                    })}
                >
                    {actions.map((action: TAction) => (
                        <button
                            title={getActionAriaLabel(action)}
                            type="button"
                            key={action}
                            className={classNames("c-image-container__action", {
                                "c-image-container__action--enlarge": action === "enlarge",
                                "c-image-container__action--delete": action === "delete",
                                "c-image-container__action--see-more": action === "see-more",
                            })}
                            tabIndex={0}
                            onClick={e => handleActionClick(e, action)}
                            aria-label={getActionAriaLabel(action)}
                        >
                            {action === "enlarge" && <ExpandIcon />}
                            {action === "delete" && <TrashIcon />}
                            {action === "see-more" && <GridIcon />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
